import { useEffect, useState } from 'react';
import { useFincaId } from '../context/FincaContext';
import api from '../api/client';
import PiscinaBuscador from '../components/PiscinaBuscador';
import ImportarDosisModal from '../components/ImportarDosisModal'; // ✅ NUEVO

const KG_POR_SACO = 25;

function calcularSacos(kg) {
  const n = Number(kg);
  if (!n || isNaN(n)) return null;
  return n / KG_POR_SACO;
}

function getSemanaISO(fecha) {
  const [anio, mes, dia] = fecha.split('-').map(Number);
  const d = new Date(anio, mes - 1, dia);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

function calcularHorasEfectivas(horaInicio, horaFin, horasPausa) {
  if (!horaInicio || !horaFin) return null;
  const [hi, mi] = horaInicio.split(':').map(Number);
  const [hf, mf] = horaFin.split(':').map(Number);
  let inicioMin = hi * 60 + mi;
  let finMin = hf * 60 + mf;
  if (finMin <= inicioMin) finMin += 24 * 60;
  const totalMinutos = finMin - inicioMin;
  let horasEfectivas = totalMinutos / 60;
  const pausa = Number(horasPausa) || 0;
  horasEfectivas = Math.max(0, horasEfectivas - pausa);
  return horasEfectivas;
}

const textoModo = {
  cronograma: 'Cronograma',
  sonico: 'Automático',
  hibrido: 'Híbrido',
  automatico: 'Automático',
};
const textoNivel = {
  economico: 'Económico',
  normal: 'Normal',
  agresivo: 'Agresivo',
};

export default function RegistroDiario({
  readOnly = false,
  camposEditables = [],
}) {
  const hoy = new Date().toISOString().split('T')[0];
  const fincaIdContext = useFincaId();

  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [sectorId, setSectorId] = useState('');
  const [piscinaFiltro, setPiscinaFiltro] = useState('');
  const [fechaDesde, setFechaDesde] = useState(hoy);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [cargando, setCargando] = useState(false);

  const [mensaje, setMensaje] = useState(null);

  // ✅ NUEVO: estado del modal de importación
  const [mostrarImportar, setMostrarImportar] = useState(false);

  const modoEdicion = fechaDesde === fechaHasta;

  const puedeEditarCampo = (campo) => {
    if (!readOnly) return true;
    return camposEditables.includes(campo);
  };

  const cambiarDia = (delta) => {
    const [anio, mes, dia] = fechaDesde.split('-').map(Number);
    const d = new Date(anio, mes - 1, dia);
    d.setDate(d.getDate() + delta);
    const nuevaFecha = d.toISOString().split('T')[0];
    setFechaDesde(nuevaFecha);
    setFechaHasta(nuevaFecha);
  };

  const irHoy = () => {
    const hoyStr = new Date().toISOString().split('T')[0];
    setFechaDesde(hoyStr);
    setFechaHasta(hoyStr);
  };

  useEffect(() => {
    cargarSectores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    if (sectorId) cargarPiscinas(sectorId);
    else setPiscinas([]);
    setPiscinaFiltro('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId]);

  useEffect(() => {
    if (sectorId && fechaDesde && fechaHasta) cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta, sectorId, piscinaFiltro, piscinas.length]);

  const cargarSectores = async () => {
    try {
      if (!fincaIdContext) {
        setSectores([]);
        return;
      }
      const { data: secs } = await api.get(`/sectores/finca/${fincaIdContext}`);
      setSectores(Array.isArray(secs) ? secs : []);
      if (secs.length > 0) setSectorId(secs[0].id);
    } catch (err) {
      console.error('Error cargando sectores:', err);
    }
  };

  const cargarPiscinas = async (id) => {
    try {
      const { data } = await api.get(`/piscinas/sector/${id}?incluirInactivas=true`);
      // ✅ Solo mostrar piscinas ACTIVAS en Registro Diario
      //    (las desactivadas solo deben aparecer en reportes históricos)
      setPiscinas(Array.isArray(data) ? data.filter((p) => p.activo !== false) : []);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const params = { sector_id: sectorId };
      if (piscinaFiltro) params.piscina_id = piscinaFiltro;

      if (modoEdicion) {
        params.fecha = fechaDesde;
      } else {
        params.desde = fechaDesde;
        params.hasta = fechaHasta;
      }

      const { data } = await api.get('/registro-diario', { params });

      if (modoEdicion) {
        setRegistros(data);
        setHistorial([]);
      } else {
        setHistorial(data);
        setRegistros([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  };

  const actualizarCelda = (piscinaId, campo, valor) => {
    if (!puedeEditarCampo(campo)) return;
    setRegistros((prev) => {
      const idx = prev.findIndex((r) => r.piscina_id === piscinaId);
      if (idx >= 0) {
        const copia = [...prev];
        copia[idx] = { ...copia[idx], [campo]: valor };
        return copia;
      }
      return [
        ...prev,
        {
          id: null,
          fecha: fechaDesde,
          semana: getSemanaISO(fechaDesde),
          sector_id: sectorId,
          piscina_id: piscinaId,
          [campo]: valor,
        },
      ];
    });
  };

  const guardarFila = async (piscinaId, campoOrigen) => {
    if (campoOrigen && !puedeEditarCampo(campoOrigen)) return;
    const reg = registros.find((r) => r.piscina_id === piscinaId);
    if (!reg) return;

    const payload = {
      fecha: reg.fecha || fechaDesde,
      semana: reg.semana || getSemanaISO(fechaDesde),
      sector_id: sectorId,
      piscina_id: piscinaId,
      modo_alimentacion: reg.modo_alimentacion || null,
      nivel_alimentacion: reg.nivel_alimentacion || null,
      hora_inicio: reg.hora_inicio || '10:00',
      horas_pausa: reg.horas_pausa || null,
      inicio_hora_pausa: reg.inicio_hora_pausa || null,
      observaciones: reg.observaciones || null,
    };

    try {
      if (reg.id) {
        const { data } = await api.put(`/registro-diario/${reg.id}`, payload);
        setRegistros((prev) =>
          prev.map((r) => (r.piscina_id === piscinaId ? { ...r, ...data } : r))
        );
      } else {
        const { data } = await api.post('/registro-diario', payload);
        setRegistros((prev) =>
          prev.map((r) =>
            r.piscina_id === piscinaId
              ? { ...r, ...data, dosis_proyectada_kg: r.dosis_proyectada_kg }
              : r
          )
        );
      }
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'Error al guardar' });
    }
  };

  const piscinasVisibles = piscinaFiltro
    ? piscinas.filter((p) => p.id === piscinaFiltro)
    : piscinas;

  const filas = piscinasVisibles.map((p) => {
    const reg = registros.find((r) => r.piscina_id === p.id);
    return { piscina: p, registro: reg || null };
  });

  return (
    <div className="p-1">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-primary">
          Registro Diario
          {readOnly && camposEditables.length === 0 && (
            <span className="ml-3 text-sm font-normal text-gray-500">(solo lectura)</span>
          )}
          {readOnly && camposEditables.length > 0 && (
            <span className="ml-3 text-sm font-normal text-amber-600">
              (edición parcial: {camposEditables.length} columnas)
            </span>
          )}
        </h1>

        {/* ✅ NUEVO: botón para importar desde Excel */}
        <button
          type="button"
          onClick={() => setMostrarImportar(true)}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium flex items-center gap-2"
        >
          📥 Importar desde Excel
        </button>
      </div>

      <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap gap-3 items-end relative z-30">
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 mb-1">Fecha</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => cambiarDia(-1)}
              className="px-3 py-2 bg-primary text-white rounded hover:bg-blue-800 text-sm font-bold"
            >
              ←
            </button>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value);
                setFechaHasta(e.target.value);
              }}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
            <button
              type="button"
              onClick={() => cambiarDia(1)}
              className="px-3 py-2 bg-primary text-white rounded hover:bg-blue-800 text-sm font-bold"
            >
              →
            </button>
            <button
              type="button"
              onClick={irHoy}
              className="px-3 py-2 border border-primary text-primary rounded hover:bg-blue-50 text-xs font-medium"
            >
              Hoy
            </button>
          </div>
        </div>

        <label className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 mb-1">Hasta</span>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </label>

        <label className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 mb-1">Sector</span>
          <select
            value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm min-w-[150px]"
          >
            <option value="">-- Selecciona --</option>
            {sectores.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </label>

        <div className="flex flex-col min-w-[180px]">
          <PiscinaBuscador
            piscinas={piscinas}
            value={piscinaFiltro}
            onChange={(id) => setPiscinaFiltro(id)}
            disabled={!sectorId}
            permitirTodas={true}
            valorTodas=""
            labelTodas="Todas las piscinas"
          />
        </div>

        <div className="text-sm text-gray-700 bg-blue-50 px-4 py-2 rounded-lg">
          <span className="text-gray-500">Semana:</span>{' '}
          <strong className="text-primary text-lg">
            {modoEdicion && fechaDesde ? getSemanaISO(fechaDesde) : 'Rango'}
          </strong>
          {!modoEdicion && (
            <span className="ml-2 text-xs text-blue-700">(modo histórico)</span>
          )}
        </div>
      </div>

      {mensaje && (
        <div className={`mb-3 p-2 rounded text-sm ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {mensaje.texto}
        </div>
      )}

      {!sectorId && !cargando && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Selecciona un sector.
        </div>
      )}

      {sectorId && modoEdicion && !cargando && (
        <div className="bg-white rounded shadow overflow-hidden">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            <table className="w-full text-xs" style={{ minWidth: '2300px' }}>
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <Th rowSpan={2}>Piscina</Th>
                  <Th rowSpan={2}>Modo Alimentación</Th>
                  <Th rowSpan={2}>Nivel Alimentación</Th>
                  <Th rowSpan={2}>Hora Inicio</Th>

                  <Th colSpan={2} centro>Dosis Proyectada (kg) 🔬</Th>
                  <Th colSpan={2} centro>Dosificado Pondlogs</Th>
                  <Th rowSpan={2}>% Ab Dosificado</Th>
                  <Th colSpan={2} centro>Total Dosif. Pondlogs Día Ant.</Th>
                  <Th rowSpan={2}>Nivel Alim. Día Ant.</Th>
                  <Th rowSpan={2}>% Ab Dosificado Día Ant.</Th>
                  <Th rowSpan={2}>Horas Efectivas</Th>
                  <Th rowSpan={2}>Horas Pausa</Th>
                  <Th rowSpan={2}>Inicio Hora Pausa</Th>
                  <Th rowSpan={2}>Observación</Th>
                </tr>
                <tr>
                  <Th centro>Kg</Th>
                  <Th centro>Sacos</Th>
                  <Th centro>Kg</Th>
                  <Th centro>Sacos</Th>
                  <Th centro>Kg</Th>
                  <Th centro>Sacos</Th>
                </tr>
              </thead>
              <tbody>
                {filas.map(({ piscina, registro }) => {
                  const reg = registro || {};
                  const dosificadoDiaAnt = reg.total_dosificado_anterior ?? reg.dosificado_dia_anterior ?? '';
                  const nivelDiaAnt = reg.nivel_alimentacion_anterior || reg.nivel_dia_anterior || '';
                  const pctAb = reg.dosificado_pondlogs && reg.dosis_proyectada_kg
                    ? (Number(reg.dosificado_pondlogs) / Number(reg.dosis_proyectada_kg)) * 100
                    : null;
                  const pctAbAnt = dosificadoDiaAnt && reg.dosis_proyectada_kg
                    ? (Number(dosificadoDiaAnt) / Number(reg.dosis_proyectada_kg)) * 100
                    : null;
                  const horasEfectivas = calcularHorasEfectivas(
                    reg.biologo_hora_inicio,
                    reg.biologo_hora_fin,
                    reg.horas_pausa
                  );

                  const sacosProyectada = calcularSacos(reg.dosis_proyectada_kg);
                  const sacosDosificado = calcularSacos(reg.dosificado_pondlogs);
                  const sacosDiaAnt = calcularSacos(dosificadoDiaAnt);
                  const dietaDesdeBiologo = reg.dieta_desde_biologo === true;

                  return (
                    <tr key={piscina.id} className="border-b hover:bg-blue-50">
                      <Td centro>{piscina.codigo}</Td>
                      <Td centro>{textoModo[reg.modo_alimentacion] || '-'}</Td>
                      <Td centro>{textoNivel[reg.nivel_alimentacion] || '-'}</Td>

                      <TdCalc violeta title="Valor del Registro Biólogo">
                        {reg.biologo_hora_inicio || '-'}
                      </TdCalc>

                      <TdCalc violeta title={dietaDesdeBiologo ? 'Valor del Registro Biólogo' : 'Sin registro del biólogo'}>
                        {reg.dosis_proyectada_kg ? Number(reg.dosis_proyectada_kg).toFixed(2) : '-'}
                      </TdCalc>
                      <TdCalc verde>
                        {sacosProyectada != null ? sacosProyectada.toFixed(2) : '-'}
                      </TdCalc>

                      <TdCalc amarillo>
                        {reg.dosificado_pondlogs ? Number(reg.dosificado_pondlogs).toFixed(2) : '-'}
                      </TdCalc>
                      <TdCalc verde>
                        {sacosDosificado != null ? sacosDosificado.toFixed(2) : '-'}
                      </TdCalc>

                      <TdCalc>{pctAb != null ? `${pctAb.toFixed(1)}%` : '-'}</TdCalc>

                      <TdCalc>{dosificadoDiaAnt ? Number(dosificadoDiaAnt).toFixed(2) : '-'}</TdCalc>
                      <TdCalc verde>
                        {sacosDiaAnt != null ? sacosDiaAnt.toFixed(2) : '-'}
                      </TdCalc>

                      <Td centro>{textoNivel[nivelDiaAnt] || '-'}</Td>
                      <TdCalc>{pctAbAnt != null ? `${pctAbAnt.toFixed(1)}%` : '-'}</TdCalc>
                      <TdCalc
                        title={
                          reg.biologo_hora_inicio && reg.biologo_hora_fin
                            ? `${reg.biologo_hora_inicio} → ${reg.biologo_hora_fin}${
                                Number(reg.horas_pausa) > 0 ? ` − ${reg.horas_pausa}h pausa` : ''
                              }`
                            : 'Sin datos del biólogo'
                        }
                      >
                        {horasEfectivas != null ? `${horasEfectivas.toFixed(1)} h` : '-'}
                      </TdCalc>

                      <TdInput
                        type="number"
                        step="0.01"
                        value={reg.horas_pausa || ''}
                        onChange={(v) => actualizarCelda(piscina.id, 'horas_pausa', v)}
                        onBlur={() => guardarFila(piscina.id, 'horas_pausa')}
                        colIndex={1}
                        readOnly={!puedeEditarCampo('horas_pausa')}
                      />

                      <TdInput
                        type="time"
                        value={reg.inicio_hora_pausa || ''}
                        onChange={(v) => actualizarCelda(piscina.id, 'inicio_hora_pausa', v)}
                        onBlur={() => guardarFila(piscina.id, 'inicio_hora_pausa')}
                        colIndex={2}
                        readOnly={!puedeEditarCampo('inicio_hora_pausa')}
                      />

                      <TdInput
                        type="text"
                        value={reg.observaciones || ''}
                        onChange={(v) => actualizarCelda(piscina.id, 'observaciones', v)}
                        onBlur={() => guardarFila(piscina.id, 'observaciones')}
                        colIndex={3}
                        readOnly={!puedeEditarCampo('observaciones')}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ✅ NUEVO: modal de importación */}
      {mostrarImportar && (
        <ImportarDosisModal
          onClose={() => setMostrarImportar(false)}
          onSuccess={() => {
            // Recargar la tabla del Registro Diario al terminar
            cargarDatos();
            setMensaje({ tipo: 'ok', texto: 'Registros importados correctamente.' });
          }}
        />
      )}
    </div>
  );
}

function Th({ children, colSpan, rowSpan, centro }) {
  return (
    <th
      colSpan={colSpan}
      rowSpan={rowSpan}
      className={`px-2 py-2 font-semibold border-r border-gray-200 whitespace-nowrap ${centro ? 'text-center' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({ children, centro }) {
  return (
    <td className={`px-2 py-1 border-r border-gray-200 whitespace-nowrap ${centro ? 'text-center' : ''}`}>
      {children}
    </td>
  );
}

function TdInput({
  value, onChange, onBlur,
  type = 'text', step, color,
  dataRow, dataField, colIndex,
  readOnly = false,
}) {
  const bg = color === 'amarillo' ? 'bg-yellow-50' : 'bg-white';

  if (readOnly) {
    return (
      <td className={`px-2 py-1.5 border-r border-gray-200 text-center ${bg}`}>
        {value !== null && value !== undefined && value !== ''
          ? String(value)
          : '-'}
      </td>
    );
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const inputs = Array.from(
        document.querySelectorAll(`input[data-col="${colIndex}"]`)
      );
      const idx = inputs.indexOf(e.target);
      if (idx >= 0 && idx < inputs.length - 1) {
        const siguiente = inputs[idx + 1];
        siguiente.focus();
        if (siguiente.select) {
          try { siguiente.select(); } catch (_) { /* noop */ }
        }
      } else {
        e.target.blur();
      }
    }
  };

  return (
    <td className={`p-0 border-r border-gray-200 ${bg}`} style={{ minWidth: type === 'time' ? 100 : 90 }}>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        data-row={dataRow}
        data-field={dataField}
        data-col={colIndex}
        className={`w-full px-2 py-1.5 bg-transparent focus:outline-none focus:bg-blue-50 text-xs ${bg}`}
        style={{ minWidth: type === 'time' ? 90 : 80, height: 26 }}
      />
    </td>
  );
}

function TdCalc({ children, amarillo, verde, violeta, title }) {
  const bg = violeta ? 'bg-purple-50' : verde ? 'bg-green-50' : amarillo ? 'bg-yellow-50' : 'bg-gray-50';
  return (
    <td
      title={title}
      className={`px-2 py-1 border-r border-gray-200 text-center font-semibold whitespace-nowrap ${bg}`}
    >
      {children}
    </td>
  );
}